import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Global404 } from 'ui-components/src';
import useMfeFeatureFlag from '../hooks/useMfeFeatureFlag';
// Shared MFE registry — single source of truth (also read by njs + rootConfig.js).
import mfeConfig from '../../../../../public/ECMv6/assets/js/mfe/mfes.json';
import './mfe-error.css';

/**
 * Shared host for every single-spa MFE route.
 *
 * Responsibilities (one place for all MFEs so none has to reimplement them):
 *  - Feature-flag gate via useMfeFeatureFlag(mfeId) (declared in mfes.json).
 *  - ONE outer layout container (`#mfe-content` / `.mfe-host`) that carries the
 *    viewport-fill + background CSS, so no MFE has to adjust its own layout and
 *    there is never blank white space while a bundle loads or errors.
 *  - The EXISTING per-MFE mount + error divs nested INSIDE that container, using
 *    each MFE's configured `domElementId` (e.g. `ispm-mfe-content`). single-spa
 *    mounts into these and rootConfig's error handler targets `*-mfe-error`, so
 *    nothing that already resolves those ids breaks.
 *
 * Loading UI: intentionally renders NO spinner here. The single, env-agnostic
 * blue-arc spinner injected by rootConfig.js is the only loading indicator
 * (it shows while the route is active and the bundle downloads). During the
 * brief flag check we render the empty container so that spinner remains the
 * sole visual.
 */
const MfeHost = ({ mfeId, label, header }) => {
  const location = useLocation();
  const { isMfeEnabled, isLoading, isError } = useMfeFeatureFlag(mfeId);

  // A disabled/errored flag means this route will never render a mount div, so
  // single-spa's app loader would otherwise leave the blue-arc spinner running
  // until its 5s DOM-poll timeout. Hide it as soon as we know we're showing 404.
  const show404 = isError || (!isLoading && !isMfeEnabled);
  useEffect(() => {
    if (show404) {
      document
        .getElementById('mfe-loading-spinner')
        ?.classList.remove('mfe-spinner-visible');
    }
  }, [show404]);

  if (show404) {
    return <Global404 path={location} />;
  }

  // Content div id must match the MFE's domElementId in mfes.json exactly so
  // single-spa mounts into it; error div follows rootConfig's naming convention.
  const contentId = mfeConfig[mfeId]?.domElementId || `${mfeId}-mfe-content`;
  const errorId = contentId.replace('-content', '-error');

  return (
    <>
      {header}
      {isMfeEnabled && (
        <div id="mfe-content" className="mfe-host">
          <div id={errorId} role="alert" aria-live="polite" />
          <div id={contentId} role="main" aria-label={label} />
        </div>
      )}
    </>
  );
};

MfeHost.propTypes = {
  /** MFE key in mfes.json (e.g. "ispm", "integration", "legacy-migration"). */
  mfeId: PropTypes.string.isRequired,
  /** Accessible label for the mount container's role="main". */
  label: PropTypes.string.isRequired,
  /** Host header element to render above the MFE content (usually <RenderHeader/>). */
  header: PropTypes.node,
};

MfeHost.defaultProps = {
  header: null,
};

export default MfeHost;
